import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getWorkerMe, updateWorkerMe, setWorkerStatus } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";

/**
 * Worker self-service: view + edit + toggle status.  Depends on the
 * `nn_worker_session` HttpOnly cookie set at registration.  If no session,
 * we surface a "Join" CTA.
 */
export default function AvailableTodayMyProfilePage() {
  const [worker, setWorker] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | signed-out | ok | error
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = async () => {
    try {
      const r = await getWorkerMe();
      setWorker(r.worker);
      setStatus("ok");
    } catch (e) {
      if (e?.response?.status === 401) {
        setStatus("signed-out");
      } else {
        setError(e?.message || "Failed to load your profile.");
        setStatus("error");
      }
    }
  };

  useEffect(() => { load(); }, []);

  const toggleStatus = async () => {
    if (!worker) return;
    const next = !worker.available_today;
    try {
      await setWorkerStatus(next);
      setWorker({ ...worker, available_today: next });
      setToast(next ? "🟢 You're back on the marketplace" : "🔴 You're hidden from the marketplace");
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not update status.");
    }
  };

  const saveField = async (patch) => {
    setSaving(true);
    try {
      const r = await updateWorkerMe(patch);
      setWorker(r.worker);
      setToast("Saved");
      setTimeout(() => setToast(null), 2000);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (status === "loading") {
    return <div style={{ padding: 60, textAlign: "center", color: "#5b5e69" }} data-testid="worker-profile-loading">Loading your profile…</div>;
  }

  if (status === "signed-out") {
    return (
      <div style={pageWrap} data-testid="worker-signed-out">
        <div style={{ maxWidth: 560, margin: "80px auto", textAlign: "center" }}>
          <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>AVAILABLE TODAY</div>
          <h1 style={h1}>NO PROFILE ON THIS DEVICE</h1>
          <p style={{ color: "#5b5e69", marginBottom: 24 }}>
            We didn&apos;t find a NearNow worker profile signed in on this browser. If you already have a profile, open it on the same phone or laptop you signed up on. Cross-device sign-in is coming soon.
          </p>
          <Link to="/available-today/join" data-testid="worker-join-cta" style={btnPrimaryLink}>
            CREATE A FREE PROFILE →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap} data-testid="worker-profile-page">
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>AVAILABLE TODAY · MY PROFILE</div>
        <h1 style={h1}>HELLO {worker?.name?.split(" ")[0]?.toUpperCase()}</h1>

        {toast && <div style={{ background: NAVY, color: GOLD, padding: "10px 16px", borderRadius: 10, marginBottom: 14, display: "inline-block" }} data-testid="worker-toast">{toast}</div>}
        {error && <div style={errBox} data-testid="worker-error">{error}</div>}

        <div style={statusCard(worker.available_today)}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: 2, opacity: 0.7 }}>CURRENT STATUS</div>
            <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 32, letterSpacing: 2 }}>
              {worker.available_today ? "🟢 AVAILABLE TODAY" : "🔴 UNAVAILABLE"}
            </div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
              {worker.available_today
                ? "Contractors on the $149+ NearNow plan can see and contact you."
                : "You're hidden from the marketplace. Tap to go live again."}
            </div>
          </div>
          <button onClick={toggleStatus} data-testid="worker-status-toggle"
            style={{
              background: worker.available_today ? "#fff" : GREEN,
              color: worker.available_today ? NAVY : "#fff",
              border: "none", padding: "16px 26px", borderRadius: 12,
              fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 16,
              fontWeight: 700, cursor: "pointer",
            }}>
            {worker.available_today ? "GO OFFLINE" : "GO LIVE"}
          </button>
        </div>

        <div style={card}>
          <h2 style={h2}>PROFILE SUMMARY</h2>
          <SummaryRow label="Skills" value={(worker.skills || []).join(" · ") || "—"} />
          <SummaryRow label="Hourly rate" value={worker.hourly_rate_usd ? `$${worker.hourly_rate_usd}/hr` : "—"} />
          <SummaryRow label="Years experience" value={worker.years_experience ?? "—"} />
          <SummaryRow label="City" value={`${worker.city || "—"}${worker.state ? `, ${worker.state}` : ""}`} />
          <SummaryRow label="Max travel" value={worker.max_travel_miles ? `${worker.max_travel_miles} mi` : "—"} />
          <SummaryRow label="Transport" value={worker.transport || "—"} />
          <SummaryRow label="Languages" value={(worker.languages || []).join(", ") || "—"} />
          <SummaryRow label="Availability" value={(worker.availability_buckets || []).join(" · ") || "—"} />
          <SummaryRow label="OSHA" value={worker.osha_certified ? "✔ Certified" : "—"} />
          <SummaryRow label="Contact" value={`${worker.phone || ""} · ${worker.email || ""}`} />
        </div>

        <div style={card}>
          <h2 style={h2}>QUICK EDIT</h2>
          <QuickEditField label="Hourly rate ($)" type="number" value={worker.hourly_rate_usd}
            onSave={(v) => saveField({ hourly_rate_usd: Number(v) })} disabled={saving} testid="quick-rate" />
          <QuickEditField label="Max travel (miles)" type="number" value={worker.max_travel_miles}
            onSave={(v) => saveField({ max_travel_miles: Number(v) })} disabled={saving} testid="quick-radius" />
          <QuickEditField label="City" value={worker.city} onSave={(v) => saveField({ city: v })} disabled={saving} testid="quick-city" />
          <QuickEditField label="Resume URL" value={worker.resume_url} onSave={(v) => saveField({ resume_url: v })} disabled={saving} testid="quick-resume" />
          <p style={{ color: "#5b5e69", fontSize: 12, marginTop: 12 }}>
            Full editor coming in Phase 2. For now use these quick fields — everything else you set at sign-up stays as-is until then.
          </p>
        </div>
      </div>
    </div>
  );
}

const SummaryRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f0f2f5" }}>
    <span style={{ color: "#5b5e69", fontSize: 12, letterSpacing: 1.5, fontWeight: 700 }}>{label.toUpperCase()}</span>
    <span style={{ color: NAVY, fontSize: 14, fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{value}</span>
  </div>
);

const QuickEditField = ({ label, value, onSave, type = "text", disabled, testid }) => {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f0f2f5" }}>
      <div style={{ flex: 1, color: "#5b5e69", fontSize: 12, letterSpacing: 1.5, fontWeight: 700 }}>{label.toUpperCase()}</div>
      <input type={type} value={v} onChange={(e) => setV(e.target.value)} disabled={disabled}
             data-testid={`${testid}-input`}
             style={{ flex: 2, padding: "10px 14px", border: "1px solid #d0d3da", borderRadius: 8, fontSize: 14 }} />
      <button onClick={() => onSave(v)} disabled={disabled} data-testid={`${testid}-save`}
              style={{ background: NAVY, color: GOLD, border: "none", padding: "10px 14px", borderRadius: 8, fontWeight: 700, letterSpacing: 1, cursor: "pointer", fontSize: 12 }}>
        SAVE
      </button>
    </div>
  );
};

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", padding: "40px 20px" };
const card = { background: "#fff", borderRadius: 16, padding: "24px 26px", border: "1px solid #e5e7ee", marginBottom: 20 };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 44, color: NAVY, margin: "6px 0 22px" };
const h2 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 22, color: NAVY, margin: "0 0 12px" };
const statusCard = (available) => ({
  background: available ? GREEN : "#3a3d47",
  color: "#fff", padding: "22px 26px", borderRadius: 16, marginBottom: 20,
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap",
});
const errBox = { color: RED, background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
const btnPrimaryLink = { display: "inline-block", background: GOLD, color: NAVY, padding: "16px 26px", borderRadius: 12, textDecoration: "none", fontWeight: 700, letterSpacing: 2, fontFamily: "Bebas Neue, Arial, sans-serif" };
